import { type ChangeEvent, type ClipboardEvent, type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Modal from '@/components/shared/Modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import apiService from '@/services/apiService';
import { getCurrentPremiumRatePercentage } from '@/services/utils';
import { getValidationMessage, validatePremiumPercentage } from '@/services/validators';

interface FormData {
	premium_rate_percentage: string;
}

interface Errors {
	premium_rate_percentage?: string;
}

interface SubmitStatus {
	type: string;
	message: string;
}

interface ModalPremiumPercentageProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}

function formatPremiumInput(value: number): string {
	if (!Number.isFinite(value)) return '';
	const rounded = Math.round(value * 100) / 100;
	return String(rounded);
}

const ModalPremiumPercentage = ({ open, onOpenChange, onSuccess }: ModalPremiumPercentageProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { appData } = useAppContext();
	const [formData, setFormData] = useState<FormData>({
		premium_rate_percentage: ''
	});
	const [errors, setErrors] = useState<Errors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({ type: '', message: '' });

	const rawPremium = getCurrentPremiumRatePercentage(appData);
	const hasDisplayedPremium =
		rawPremium != null && !Number.isNaN(Number(rawPremium)) && Number(rawPremium) !== 0;

	useEffect(() => {
		if (!open) return;

		setErrors({});
		setSubmitStatus({ type: '', message: '' });

		if (rawPremium != null && !Number.isNaN(Number(rawPremium)) && Number(rawPremium) !== 0) {
			setFormData({ premium_rate_percentage: formatPremiumInput(Number(rawPremium)) });
		} else {
			setFormData({ premium_rate_percentage: '' });
		}
	}, [open, rawPremium]);

	const validateForm = (): boolean => {
		// Convert comma to dot for validation
		const normalizedValue = formData.premium_rate_percentage.trim().replace(',', '.');
		const errorKey = validatePremiumPercentage(normalizedValue);

		if (errorKey) {
			const errorMessage = getValidationMessage(errorKey);
			setErrors({ premium_rate_percentage: errorMessage });
			return false;
		}

		setErrors({});
		return true;
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { id, value } = e.target;

		// Allow decimal input for premium percentage (0-100), max 6 characters (e.g., "100.00")
		if (id === 'premium_rate_percentage') {
			// Allow digits, single decimal point or comma (but not both)
			// Check if both comma and dot exist - if so, prevent the change
			const hasComma = value.includes(',');
			const hasDot = value.includes('.');
			if (hasComma && hasDot) {
				return;
			}

			// Allow digits, single decimal point or comma, and ensure it's a valid decimal format
			if (!/^[0-9]*[,.]?[0-9]*$/.test(value) || value.length > 6) {
				return;
			}
			// Convert comma to dot for validation, but keep original in display
			const normalizedValue = value.replace(',', '.');
			// Prevent values over 100
			const numValue = Number.parseFloat(normalizedValue);
			if (!Number.isNaN(numValue) && numValue > 100) {
				return;
			}
		}

		setFormData((prevState) => ({
			...prevState,
			[id]: value
		}));
		// Clear error when user starts typing
		if (errors[id as keyof Errors]) {
			setErrors((prev) => ({ ...prev, [id]: '' }));
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		const { id } = e.target as HTMLInputElement;
		if (id === 'premium_rate_percentage') {
			e.preventDefault();
			const pastedText = e.clipboardData.getData('text');
			// Filter to allow only digits, decimal point, and comma
			const filteredValue = pastedText.replace(/[^0-9,.]/g, '').slice(0, 6);
			// Normalize to dot for validation, but preserve comma if only one separator exists
			const normalizedForValidation = filteredValue.replace(',', '.');
			// Ensure only one decimal separator (dot or comma)
			const hasComma = filteredValue.includes(',');
			const hasDot = filteredValue.includes('.');
			let decimalValue = filteredValue;

			if (hasComma && hasDot) {
				// If both exist, use the first one and remove the rest
				const commaIndex = filteredValue.indexOf(',');
				const dotIndex = filteredValue.indexOf('.');
				if (commaIndex < dotIndex) {
					decimalValue = filteredValue.replace(/\./g, '');
				} else {
					decimalValue = filteredValue.replace(/,/g, '');
				}
			} else if (hasComma || hasDot) {
				// Only one separator exists, ensure only one instance
				const separator = hasComma ? ',' : '.';
				const parts = filteredValue.split(separator);
				decimalValue =
					parts.length > 2
						? `${parts[0]}${separator}${parts.slice(1).join('')}`
						: parts.length === 2
							? `${parts[0]}${separator}${parts[1].slice(0, 2)}`
							: filteredValue;
			}

			// Check if value is within range using normalized value
			const numValue = Number.parseFloat(normalizedForValidation);
			if (!Number.isNaN(numValue) && numValue <= 100) {
				setFormData(
					(prevState) =>
						({
							...prevState,
							[id]: decimalValue
						}) as FormData
				);
				if (errors.premium_rate_percentage) {
					setErrors((prev) => ({ ...prev, premium_rate_percentage: '' }));
				}
			}
		}
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (!id) {
			setErrors({ premium_rate_percentage: t('modals.premiumPercentage.noIdError') });
			return;
		}

		setIsSubmitting(true);
		setSubmitStatus({ type: '', message: '' });

		try {
			let payload: { current_premium_percentage: number | null };

			// If field is empty, send null to clear the value
			if (!formData.premium_rate_percentage.trim()) {
				payload = {
					current_premium_percentage: null
				};
			} else {
				// Convert comma to dot before parsing
				const normalizedValue = formData.premium_rate_percentage.trim().replace(',', '.');
				payload = {
					current_premium_percentage: Number.parseFloat(normalizedValue)
				};
			}

			await apiService.premiumPercentage.update(id, payload);
			onOpenChange(false);
			onSuccess?.();
		} catch (error) {
			setSubmitStatus({
				type: 'error',
				message: t('common.error')
			});
			console.error('Premium percentage submit failed', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			id="modalPremiumPercentage"
			open={open}
			onOpenChange={onOpenChange}
			size="md"
			backdrop={true}
			keyboard={true}
			closeButton={true}
		>
			<div className="py-6 px-4">
				<div className="text-2xl font-bold mb-3">
					{hasDisplayedPremium
						? t('modals.premiumPercentage.titleEdit')
						: t('modals.premiumPercentage.title')}
				</div>
				<p className="mb-6 text-muted-foreground leading-relaxed">
					{hasDisplayedPremium ? (
						<span className="block">{t('modals.premiumPercentage.introductionEdit')}</span>
					) : (
						<>
							<span className="sm:inline-block mr-1">{t('modals.premiumPercentage.subtitle')}</span>
							<span className="sm:inline-block">{t('modals.premiumPercentage.introduction')}</span>
						</>
					)}
				</p>

				{submitStatus.message && (
					<Alert
						variant={submitStatus.type === 'success' ? 'success' : 'destructive'}
						className="mb-6"
					>
						<AlertDescription>{submitStatus.message}</AlertDescription>
					</Alert>
				)}

				<form onSubmit={handleSubmit} noValidate className="space-y-4 text-left">
					<Field data-invalid={!!errors.premium_rate_percentage}>
						<FieldLabel htmlFor="premium_rate_percentage">
							{t('modals.premiumPercentage.inputLabel')}
						</FieldLabel>
						<div className="relative">
							<span className="absolute left-3 top-5 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
								%
							</span>
							<Input
								type="text"
								id="premium_rate_percentage"
								value={formData.premium_rate_percentage}
								onChange={handleChange}
								onPaste={handlePaste}
								disabled={isSubmitting}
								placeholder={t('modals.premiumPercentage.inputPlaceholder')}
								data-testid="modal-premium-percentage-input"
								aria-invalid={!!errors.premium_rate_percentage}
								className={cn('pl-8', errors.premium_rate_percentage && 'border-destructive')}
							/>
							{errors.premium_rate_percentage && (
								<FieldError data-testid="input-feedback-premium-percentage">
									{errors.premium_rate_percentage}
								</FieldError>
							)}
						</div>
					</Field>

					<Button
						type="submit"
						className="w-full mt-6"
						size="lg"
						loading={isSubmitting}
						disabled={isSubmitting}
						data-testid="modal-premium-percentage-submit-button"
					>
						{t('modals.premiumPercentage.submitButton')}
					</Button>
					<Button
						type="button"
						variant="ghost"
						className="w-full"
						disabled={isSubmitting}
						onClick={() => onOpenChange(false)}
						data-testid="modal-premium-percentage-later-button"
					>
						{t('modals.premiumPercentage.laterButton')}
					</Button>
				</form>
			</div>
		</Modal>
	);
};

export default ModalPremiumPercentage;
